import { useRef, useState, useEffect, useCallback } from "react"

const THRESHOLD = 80
const MAX_PULL = 150

export interface UsePwaRefreshReturn {
  containerRef: React.RefObject<HTMLDivElement | null>
  pullDistance: number
  isRefreshing: boolean
}

export function usePwaRefresh(): UsePwaRefreshReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const startYRef = useRef(0)
  const pullingRef = useRef(false)
  const refreshingRef = useRef(false)

  const ease = useCallback((distance: number) => {
    return MAX_PULL * (1 - Math.exp((-1.5 * distance) / MAX_PULL))
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    // Only activate on iOS standalone PWA — Android has native pull-to-refresh
    const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    if (!isIos || !isStandalone) return

    el.style.overscrollBehavior = "none"

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return
      if (window.scrollY > 0) return
      if (e.touches.length !== 1) return
      startYRef.current = e.touches[0].clientY
      pullingRef.current = true
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || refreshingRef.current) return
      const dy = e.touches[0].clientY - startYRef.current
      if (dy <= 0) {
        setPullDistance(0)
        return
      }
      e.preventDefault()
      setPullDistance(ease(dy))
    }

    const onTouchEnd = () => {
      if (!pullingRef.current) return
      pullingRef.current = false

      setPullDistance((current) => {
        if (current >= THRESHOLD && !refreshingRef.current) {
          refreshingRef.current = true
          setIsRefreshing(true)
          window.location.reload()
          return current
        }
        return 0
      })
    }

    el.addEventListener("touchstart", onTouchStart, { passive: true })
    el.addEventListener("touchmove", onTouchMove, { passive: false })
    el.addEventListener("touchend", onTouchEnd, { passive: true })

    return () => {
      el.style.overscrollBehavior = ""
      el.removeEventListener("touchstart", onTouchStart)
      el.removeEventListener("touchmove", onTouchMove)
      el.removeEventListener("touchend", onTouchEnd)
    }
  }, [ease])

  return { containerRef, pullDistance, isRefreshing }
}
